import { type FC, lazy } from 'react'
import { Route, Routes as RouterRoutes, Navigate } from 'react-router-dom'

import { useIsMobileVariant } from '@dhl-ui/components'
import { NavigateExternal } from '@dhl/utils'

import { RequireIsOnboarded, RequireIsOnboarding, SetupRoutes } from '~/setup/index.js'
import { PortalMainPage } from '~/components-page/index.js'

import { getConfig, isFeatureEnabled } from '~/config/index.js'
import { externalPaths, paths } from './links.js'

import { InteractionManager } from './interaction/InteractionManager.js'
import { WithNotificationSync } from './WithNotificationSync.js'
import { ProfileContextProvider } from './profile/context/ProfileContext.js'

export const rootPath = 'portal'

const DashboardRoutes = lazy(() =>
  import('~/dashboard/view/Routes.js').then((module) => ({ default: module.DashboardRoutes }))
)

const C2CRoutes = lazy(() => import('~/c2c/view/Routes.js').then((module) => ({ default: module.C2CRoutes })))
const MaintenancePage = lazy(() => import('@dhl/maintenance').then((module) => ({ default: module.MaintenancePage })))
const LocatorRoutes = lazy(() => import('~/locator/Routes.js').then((module) => ({ default: module.LocatorRoutes })))
const MoreRoutes = lazy(() => import('~/more/view/Routes.js').then((module) => ({ default: module.MoreRoutes })))
const PiecesRoutes = lazy(() => import('~/pieces/view/Routes.js').then((module) => ({ default: module.PiecesRoutes })))
const ProfileRoutes = lazy(() =>
  import('~/profile/view/Routes.js').then((module) => ({ default: module.ProfileRoutes }))
)
const SearchRoutes = lazy(() => import('~/search/view/Routes.js').then((module) => ({ default: module.SearchRoutes })))
const TrackTraceRoutes = lazy(() =>
  import('~/tracktrace/view/Routes.js').then((module) => ({ default: module.TrackTraceRoutes }))
)
const InterventionRoutes = lazy(() =>
  import('~/intervention/view/Routes.js').then((module) => ({ default: module.InterventionRoutes }))
)
const ServicepointsRoutes = lazy(() =>
  import('~/servicepoints/Routes.js').then((module) => ({ default: module.ServicepointsRoutes }))
)

export const Routes: FC = () => {
  const isMobile = useIsMobileVariant()

  const {
    features: { newProfileSetup },
  } = getConfig()
  const maintenanceModeEnabled = isFeatureEnabled('maintenance')

  if (maintenanceModeEnabled) {
    return (
      <PortalMainPage>
        <MaintenancePage />
      </PortalMainPage>
    )
  }

  return (
    <RouterRoutes>
      <Route element={<RequireIsOnboarded enabledDate={newProfileSetup.enabledDate} initialSetupRequired={isMobile} />}>
        <Route element={<WithNotificationSync />}>
          <Route element={<InteractionManager />}>
            <Route path={paths.DASHBOARD}>
              <Route index path="*" element={<DashboardRoutes />} />
            </Route>
            <Route path={paths.PIECES}>
              <Route index path="*" element={<PiecesRoutes />} />
            </Route>

            <Route path={`${paths.TRACKTRACE}/*`} element={<TrackTraceRoutes />} />

            <Route path={paths.INTERVENTIONS}>
              <Route index path="*" element={<InterventionRoutes />} />
            </Route>

            <Route path={paths.C2C}>
              <Route index path="*" element={<C2CRoutes />} />
            </Route>

            <Route path={paths.MORE}>
              <Route index path="*" element={<MoreRoutes />} />
            </Route>
            <Route path={paths.LOCATOR}>
              <Route index path="*" element={<LocatorRoutes />} />
            </Route>
            <Route path={paths.PROFILE}>
              <Route
                index
                path="*"
                element={
                  <ProfileContextProvider>
                    <ProfileRoutes />
                  </ProfileContextProvider>
                }
              />
            </Route>
            <Route path={paths.SEARCH}>
              <Route index path="*" element={<SearchRoutes />} />
            </Route>
            <Route path={paths.SERVICEPOINTS}>
              <Route index path="*" element={<ServicepointsRoutes />} />
            </Route>

            {Object.values(externalPaths).map((path) => (
              <Route key={path} path={`/${path}`} element={<NavigateExternal to={path} />} />
            ))}

            <Route path="/" element={<Navigate to={`/${paths.DASHBOARD}`} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Route>
      </Route>

      <Route
        element={<RequireIsOnboarding enabledDate={newProfileSetup.enabledDate} initialSetupRequired={isMobile} />}
      >
        <Route path={paths.SETUP}>
          <Route index path="*" element={<SetupRoutes />} />
        </Route>
        <Route path="*" element={<Navigate to="/setup" />} />
      </Route>
    </RouterRoutes>
  )
}
